/**
 * Utilitaires de formatage pour les documents juridiques
 * Fonctions partagées entre ArticleDetailPanel, DossierPage et LegalAnalysisModal
 */
import DOMPurify from 'dompurify';
import { detectCountry } from '@/services/countries';

const PURIFY_CONFIG = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 'br', 'li', 'ul', 'ol', 'p', 'div', 'span', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
  ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'rel'],
};

// ============================================
// FORMATAGE DES DATES
// ============================================

export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const locale = detectCountry() === 'IT' ? 'it-IT' : 'fr-FR';
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  } catch (e) {
    return dateString;
  }
  return dateString;
};

// Alias pour maintenir la compatibilité
export const formatBaloDate = formatDate;
export const formatActesDate = formatDate;
export const formatBodaccDate = formatDate;
export const formatInfoFiDate = formatDate;
export const formatActesSocietesDate = formatDate;
export const formatConcurrenceDate = formatDate;
export const formatInpiDate = formatDate;

// ============================================
// FORMATAGE DES CATÉGORIES ET TYPES
// ============================================

export const formatBaloCategory = (categorie) => {
  if (!categorie) return '';
  const parts = categorie.split('##');
  return parts.length > 1 ? parts[1].trim() : categorie;
};

export const formatBodaccFamily = (family) => {
  if (!family) return '';
  return family.charAt(0).toUpperCase() + family.slice(1).toLowerCase();
};

export const formatBodaccType = formatBodaccFamily;

// ============================================
// FORMATAGE DES ÉTATS (CNN)
// ============================================

export const formatEtat = (etat) => {
  if (!etat) return '';
  const etatMap = {
    'VIGUEUR_NON_ETEN': 'En vigueur (non étendu)',
    'VIGUEUR_ETEN': 'En vigueur (étendu)',
    'PERIME': 'Périmé',
    'ABROGE': 'Abrogé',
    'REMPLACE': 'Remplacé',
    'MODIFIE': 'Modifié'
  };
  return etatMap[etat] || etat;
};

export const getEtatClass = (etat) => {
  if (!etat) return 'etat-default';
  const etatLower = etat.toLowerCase();
  if (etatLower.includes('vigueur') && etatLower.includes('étendu')) return 'etat-vigueur-etendu';
  if (etatLower.includes('vigueur') || etat === 'VIGUEUR_NON_ETEN' || etat === 'VIGUEUR_ETEN') return 'etat-vigueur';
  if (etatLower.includes('périmé') || etatLower.includes('perime') || etat === 'PERIME') return 'etat-perime';
  if (etatLower.includes('abrogé') || etatLower.includes('abroge') || etat === 'ABROGE') return 'etat-abroge';
  if (etatLower.includes('remplacé') || etatLower.includes('remplace') || etat === 'REMPLACE') return 'etat-remplace';
  if (etatLower.includes('modifié') || etatLower.includes('modifie') || etat === 'MODIFIE') return 'etat-modifie';
  return 'etat-default';
};

// ============================================
// CLASSES SPÉCIFIQUES INPI ET CONCURRENCE
// ============================================

export const getInpiDomainClass = (domaine) => {
  if (!domaine) return 'domain-default';
  const d = domaine.toLowerCase();
  if (d.includes('marque')) return 'domain-marques';
  if (d.includes('brevet')) return 'domain-brevets';
  if (d.includes('dessin') || d.includes('modèle')) return 'domain-dessins';
  return 'domain-default';
};

export const getConcurrenceDecisionClass = (sens) => {
  if (!sens) return 'decision-default';
  const s = sens.toLowerCase();
  if (s.includes('sanction') || s.includes('condamn')) return 'decision-sanction';
  if (s.includes('accept') || s.includes('autoris')) return 'decision-accepted';
  if (s.includes('rejet') || s.includes('refus')) return 'decision-rejected';
  return 'decision-default';
};

// ============================================
// FORMATAGE DES TITRES ET HIÉRARCHIES
// ============================================

export const formatHierarchy = (hierarchy) => {
  if (!hierarchy) return '';
  if (Array.isArray(hierarchy)) {
    return hierarchy.join(' > ');
  }
  return hierarchy.split(' > ').map(s => s.trim()).join(' > ');
};

export const formatEuritextTitle = (title) => {
  if (!title) return '';
  if (title.toLowerCase() === 'arret') return 'Arrêt';
  return title.charAt(0).toUpperCase() + title.slice(1);
};

export const getStatutsTitle = (pdfName) => {
  if (!pdfName) return '';
  return pdfName.replace(/\.pdf$/i, '');
};

// ============================================
// GÉNÉRATION DES URLS
// ============================================

export const getActesSocietesFullUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  return `https://actespdf.blob.core.windows.net/actes-api/${relativeUrl}`;
};

export const getBaloOfficialLink = (idAnnonce, sourceData = null) => {
  let fname = null;
  if (sourceData?.fichiers_joints_fichier_pdf_text) {
    fname = sourceData.fichiers_joints_fichier_pdf_text;
  }
  if (!fname && idAnnonce && idAnnonce.length >= 12) {
    fname = `${idAnnonce}`;
  }
  if (fname) {
    try {
      const year = fname.substring(0, 4);
      const monthDay = fname.substring(4, 8);
      return `https://www.journal-officiel.gouv.fr/telechargements/BALO/pdf/${year}/${monthDay}/${fname}`;
    } catch (e) {
      /* ignore */
    }
  }
  return 'https://www.journal-officiel.gouv.fr/pages/balo/';
};

// ============================================
// LIENS OFFICIELS POUR LES DÉCISIONS
// ============================================

export const getOfficialDecisionLink = (id, title) => {
  if (!id || !title) return '#';
  const idStr = typeof id === 'string' ? id : String(id || '');

  // CETATEXT or JURITEXT
  if (idStr.startsWith('CETATEXT') || idStr.startsWith('JURITEXT')) {
    return `https://www.legifrance.gouv.fr/ceta/id/${idStr}`;
  }

  // XML decisions from CAA, TA or CE - format unifié : shareFile/{id}
  if (idStr.includes('_')) {
    const idNoXml = idStr.replace('.xml', '');
    return `https://opendata.justice-administrative.fr/recherche/shareFile/${idNoXml}`;
  }

  // Default to Cour de cassation
  return `https://www.courdecassation.fr/decision/${idStr}`;
};

// ============================================
// FORMATAGE DU TEXTE (MARKDOWN SIMPLIFIÉ)
// ============================================

export const formatText = (text) => {
  if (!text) return '';

  let formatted = text;

  // Gestion des séparateurs
  formatted = formatted.replace(/\n\s*\*\*\*\s*\n/g, '\n\n');

  // Handle Roman numerals (I), II), III), IV), etc.)
  formatted = formatted.replace(
    /^#{0,6}\s*(\*{0,2}([IVX]+)\)\*{0,2}\s*)([^\n]+)/gm,
    "<h3 style='margin-top: 1.5em !important; font-size: 1.3em; font-weight: bold; color: #625CFB;'>$1$3</h3>"
  );

  // Headings
  formatted = formatted.replace(/####\s+(.*?)(?:\n|$)/g, '<h4><strong>$1</strong></h4>');
  formatted = formatted.replace(/###\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
  formatted = formatted.replace(/##\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
  formatted = formatted.replace(/#\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');

  // Bullet points avec niveaux d'indentation
  formatted = formatted.replace(
    /(^|\n)( *)\*\s+(.*?)(?=\n|$)/g,
    (match, sep, indent, content) => {
      const level = Math.floor(indent.length / 4) + 1;
      const bullets = ['-', '•', '◦', '▪'];
      const bullet = bullets[Math.min(level - 1, 3)];
      return `${sep}<li style="margin-left:${level}em; margin-top:1.2em; margin-bottom:1.2em; padding-left:0.5em;">${bullet} ${content}</li>`;
    }
  );

  // Grouper les <li> consécutifs dans un <ul>
  formatted = formatted.replace(/(<li[\s\S]*?<\/li>)+/g, m => `<ul>${m}</ul>`);
  formatted = formatted.replace(/<\/ul>\s*<ul>/g, '');

  // Citations en italique
  formatted = formatted.replace(/"([^"]+)"/g, '"<em>$1</em>"');
  formatted = formatted.replace(/«([^»]+)»/g, '«<em>$1</em>»');

  // Line breaks et bold
  formatted = formatted.replace(/\n/g, '<br>');
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  return DOMPurify.sanitize(formatted, PURIFY_CONFIG);
};

// ============================================
// CONSTRUCTION DES DONNÉES D'ARTICLE
// ============================================

export const buildArticleDetailData = (doc, sourceKey, index) => {
  let title = doc.title || doc.textNumber || `Document #${index + 1}`;

  // Cas spéciaux pour le titre
  if (sourceKey === 'statuts' && doc.pdf_name) {
    title = getStatutsTitle(doc.pdf_name);
  } else if (sourceKey === 'bofip' && doc.identifiant) {
    title = doc.identifiant;
  } else if (sourceKey === 'reglements' && doc.title) {
    title = doc.title;
  }

  const details = {
    title: title,
    content: sourceKey === 'juritext' ? (doc.summary || '') : (doc.content || ''),
    source: sourceKey,
    sourceData: doc,
    id: doc.id,
  };

  // Ajouter les champs spécifiques selon la source
  switch (sourceKey) {
    case 'juritext':
      details.texte = doc.texte || '';
      break;
    case 'legiarti':
      details.hierarchy = doc.hierarchy;
      details.annotation = doc.annotation || '';
      break;
    case 'bofip':
      details.url = doc.url;
      break;
    case 'euritext':
      details.links = doc.links;
      break;
    case 'cnn':
      details.etat = doc.etat;
      details.date_debut = doc.date_debut;
      details.date_fin = doc.date_fin;
      details.cid_tex = doc.cid_tex;
      details.article_id = doc.article_id;
      break;
    case 'balo':
      details.id_annonce = doc.id_annonce;
      details.dateparution = doc.dateparution;
      details.societes_noms = doc.societes_noms;
      details.siren = doc.siren;
      details.numero_affaire = doc.numero_affaire;
      details.categorie_libelle = doc.categorie_libelle;
      details.parution_numero = doc.parution_numero;
      break;
    case 'statuts':
      details.pdf_name = doc.pdf_name;
      details.slugged_title = doc.slugged_title;
      break;
    case 'convbi':
      details.pays = doc.pays;
      details.titre_lien = doc.titre_lien;
      details.pdf_url = doc.pdf_url;
      details.article_num = doc.article_num;
      details.content = doc.texte || doc.content || '';
      break;
    case 'infofi':
      details.nom_societe = doc.nom_societe;
      details['Titre opération'] = doc['Titre opération'];
      details.Lien_Document = doc.Lien_Document;
      details.date_document = doc.date_document;
      details.description_information = doc.description_information;
      details.id_information = doc.id_information;
      break;
    case 'reglements':
      details.celex = doc.celex;
      details.article_number = doc.article_number;
      details.chapter_title = doc.chapter_title;
      details.date = doc.date;
      details.content = doc.texte || doc.content || '';
      break;
    case 'bodacc':
      details.doc_id = doc.doc_id;
      details.dateparution = doc.dateparution;
      details.familleavis = doc.familleavis;
      details.typeavis = doc.typeavis;
      details.url_complete = doc.url_complete;
      break;
    case 'energy':
      details.doc_id = doc.doc_id;
      details.url = doc.url;
      break;
    case 'actes_societes':
      details.siren = doc.siren;
      details.acte_id = doc.acte_id;
      details.date_acte = doc.date_acte;
      details.summary = doc.summary;
      details.keywords = doc.keywords;
      details.chunk_number = doc.chunk_number;
      details.total_pages = doc.total_pages;
      if (doc.source_path) {
            details.source_path = doc.source_path;
          } else if (doc.source && doc.source !== 'actes_societes') {
            details.source_path = doc.source;
          }
      break;
    case 'concurrence':
      details.type_document = doc.type_document;
      details.numero_affaire = doc.numero_affaire;
      details.date_decision = doc.date_decision;
      details.secteur = doc.secteur;
      details.parties = doc.parties;
      details.sens_decision = doc.sens_decision;
      details.url_page = doc.url_page;
      details.content = doc.texte_integral || doc.content || '';
      break;
    case 'inpi':
      details.domaine = doc.domaine;
      details.reference = doc.reference;
      details.juridiction = doc.juridiction;
      details.parties = doc.parties;
      details.date = doc.date;
      details.url_document_final = doc.url_document_final;
      details.content = doc.texte || doc.text_complet || doc.content || '';
      break;
    case 'cnil':
      details.nature = doc.nature;
      details.date = doc.date;
      details.titre_complet = doc.titre_complet;
      details.url = doc.link || doc.url;
      details.content = doc.summary || '';
      break;
    case 'boss':
      details.title = doc.title;
      details.date = doc.date;
      details.url = doc.link || doc.url;
      details.content = doc.summary || '';
      break;
    case 'acpr':
      details.acpr_source = doc.acpr_source;
      details.doc_subtype = doc.doc_subtype;
      details.categorie = doc.categorie;
      details.themes = doc.themes;
      details.numero = doc.numero;
      details.date = doc.date;
      details.url = doc.link || doc.url;
      details.content = doc.summary || '';
      break;
    case 'amf':
      details.amf_source = doc.amf_source;
      details.doc_type = doc.doc_type;
      details.reference = doc.reference;
      details.themes = doc.themes;
      details.montant = doc.montant;
      details.sanctioned_entity = doc.sanctioned_entity;
      details.date = doc.date;
      details.url = doc.link || doc.url;
      details.content = doc.summary || '';
      break;
    case 'eccomp':
      details.case_number = doc.case_number;
      details.case_title = doc.case_title;
      details.instrument = doc.instrument;
      details.decision_type = doc.decision_type;
      details.decision_reference = doc.decision_reference;
      details.decision_language = doc.decision_language;
      details.member_state = doc.member_state;
      details.companies = doc.companies;
      details.legal_basis = doc.legal_basis;
      details.sectors = doc.sectors;
      details.date = doc.date;
      details.year = doc.year;
      details.url = doc.link || doc.url;
      details.content = doc.summary || doc.content || '';
      details.texte = doc.full_text || doc.texte || '';
      break;
    case 'upc':
      details.case_numbers = doc.case_numbers;
      details.registry_number = doc.registry_number;
      details.court = doc.court;
      details.action_type = doc.action_type;
      details.parties = doc.parties;
      details.date = doc.date_raw || doc.date;
      details.language_of_proceedings = doc.language_of_proceedings;
      details.document_language = doc.document_language;
      details.summary = doc.summary;
      details.headnotes = doc.headnotes;
      details.keywords = doc.keywords;
      details.url = doc.detail_url || doc.pdf_url;
      details.detail_url = doc.detail_url;
      details.pdf_url = doc.pdf_url;
      details.content = doc.summary || doc.headnotes || '';
      details.texte = doc.texte || '';
      break;
    case 'edpb':
      details.title = doc.title;
      details.slug = doc.slug;
      details.publication_types = doc.publication_types;
      details.topics = doc.topics;
      details.members = doc.members;
      details.status = doc.status;
      details.date = doc.date_raw || doc.date;
      details.summary = doc.summary;
      details.key_points = doc.key_points;
      details.keywords = doc.keywords;
      details.url = doc.url || doc.pdf_url_used;
      details.pdf_url_used = doc.pdf_url_used;
      details.word_count = doc.word_count;
      details.content = doc.summary || doc.key_points || '';
      details.texte = doc.texte || '';
      break;
    case 'agcm':
      details.date = doc.date;
      details.year = doc.year;
      details.rid = doc.rid;
      details.jurisdiction = doc.jurisdiction;
      details.url = doc.link || doc.url;
      details.content = doc.summary || doc.content || '';
      details.texte = doc.full_text || doc.texte || '';
      break;
    case 'giustizia_amministrativa':
      details.date = doc.date;
      details.year = doc.year;
      details.anno = doc.anno;
      details.tipo = doc.tipo;
      details.court = doc.court;
      details.section = doc.section;
      details.jurisdiction = doc.jurisdiction;
      details.rid = doc.rid;
      details.url = doc.link || doc.url;
      details.content = doc.summary || doc.content || '';
      details.texte = doc.full_text || doc.texte || '';
      break;
    case 'jurisprudence_europe_italy':
      details.date = doc.date;
      details.year = doc.year;
      details.ecli = doc.ecli;
      details.celex = doc.celex;
      details.court = doc.court;
      details.decision_type = doc.decision_type;
      details.link_urls = doc.link_urls || {};
      details.url = doc.link || doc.url || (doc.link_urls && (doc.link_urls.CELEX || doc.link_urls.ECLI || doc.link_urls.Cellar));
      details.content = doc.summary || doc.content || '';
      details.texte = doc.full_text || doc.texte || '';
      break;
    case 'eurlex_italy':
      details.date = doc.date || doc.consolidation_date;
      details.year = doc.year;
      details.base_celex = doc.base_celex;
      details.cons_celex = doc.cons_celex;
      details.consolidation_date = doc.consolidation_date;
      details.act_type = doc.act_type;
      details.act_number = doc.act_number;
      details.act_year = doc.act_year;
      details.act_title = doc.act_title;
      details.article_identifier = doc.article_identifier;
      details.article_heading = doc.article_heading;
      details.article_title = doc.article_title;
      details.breadcrumb = doc.breadcrumb;
      details.paragraphs_count = doc.paragraphs_count;
      details.jurisdiction = doc.jurisdiction;
      details.eli = doc.eli;
      details.formex_schema = doc.formex_schema;
      details.url = doc.link || doc.url;
      details.content = doc.summary || doc.content || '';
      details.texte = doc.full_text || doc.texte || '';
      break;
  }

  return details;
};

// ============================================
// VÉRIFICATION DES RÉFÉRENCES
// ============================================

export const hasReferences = (articleData) => {
  const source = articleData.source;
  switch (source) {
    case 'juritext':
      return articleData.texte || articleData.id;
    case 'bofip':
      return articleData.url;
    case 'cnn':
      return articleData.article_id;
    case 'balo':
      return articleData.id_annonce;
    case 'euritext':
      return articleData.sourceData && articleData.sourceData.links;
    case 'statuts':
      return articleData.slugged_title;
    case 'convbi':
      return articleData.pdf_url;
    case 'infofi':
      return articleData.sourceData && articleData.sourceData.Lien_Document;
    case 'actes_societes':
      return articleData.source_path || (articleData.sourceData && (articleData.sourceData.source || articleData.sourceData.source_path));
    case 'bodacc':
      return articleData.url_complete;
    case 'reglements':
      return articleData.celex;
    case 'energy':
      return articleData.url;
    case 'concurrence':
      return articleData.url_page;
    case 'inpi':
      return articleData.url_document_final;
    case 'legiarti':
      return articleData.id;
    case 'mention':
      return articleData.original_reference || articleData.url;
    case 'cnil':
      return articleData.url || articleData.id;
    case 'boss':
      return articleData.url || articleData.id;
    case 'acpr':
      return articleData.url || articleData.id;
    case 'amf':
      return articleData.url || articleData.id;
    case 'eccomp':
      return articleData.url || articleData.link || articleData.case_number;
    case 'upc':
      return articleData.detail_url || articleData.pdf_url || articleData.url || articleData.id;
    case 'edpb':
      return articleData.url || articleData.pdf_url_used || articleData.id;
    case 'agcm':
      return articleData.url || articleData.id;
    case 'giustizia_amministrativa':
      return articleData.url || articleData.id;
    case 'jurisprudence_europe_italy':
      return articleData.url || articleData.celex || articleData.ecli || articleData.id;
    case 'eurlex_italy':
      return articleData.url || articleData.cons_celex || articleData.eli || articleData.id;
    default:
      return false;
  }
};

export const hasMetadata = (source) => {
  return ['legiarti', 'cnn', 'balo', 'bodacc', 'infofi', 'reglements', 'concurrence', 'inpi', 'eurlex_italy'].includes(source);
};

export const hasCitations = (articleData) => {
  return articleData.sourceData?.citations &&
    Object.keys(articleData.sourceData.citations).length > 0;
};
